/**
 * Drivers API Client
 * Strapi integration for drivers management
 */

import { getHttpClient } from '@ufc/core'
import type { Driver, DriversFilter, DriverStatus, PaginatedDrivers, PaginationMeta } from '../../domain/types'
import { driverStatusToBackend, DEFAULT_PAGE_SIZE } from '../../domain/types'
import {
  STRAPI_DRIVER_ENDPOINTS,
  type StrapiDriverListResponse,
  type StrapiDriverResponse,
  strapiDriverListResponseSchema,
  strapiDriverResponseSchema,
  type EntityUpdateResult,
} from '../../api/api-types'
import {
  transformDriverToStrapiCreate,
  transformDriverToStrapiUpdate,
  transformStrapiDriverToDomain,
  transformStrapiDriversToDomain,
} from '../../api/transformers'

const DEFAULT_DRIVER_POPULATE: Record<string, string> = {
  'populate[tenant]': '*',
}

export const driversApi = {
  /**
   * Get all drivers with optional filtering
   */
  async getDrivers(filter?: DriversFilter): Promise<Driver[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    if (filter?.status && filter.status !== 'all') {
      const backendStatus = driverStatusToBackend[filter.status as DriverStatus]
      params.set('filters[driverStatus][$eq]', backendStatus)
    }

    if (filter?.licenseType && filter.licenseType !== 'all') {
      // Handle both single value and array
      if (Array.isArray(filter.licenseType)) {
        filter.licenseType.forEach((type, index) => {
          params.set(`filters[$or][${index}][licenseType][$eq]`, type)
        })
      } else {
        params.set('filters[licenseType][$eq]', filter.licenseType)
      }
    }

    // Name filter (search by driver name)
    if (filter?.name) {
      params.set('filters[name][$containsi]', filter.name)
    }

    // Phone filter (search by phone number)
    if (filter?.phone) {
      params.set('filters[phone][$containsi]', filter.phone)
    }

    // Employee number filter (search by driverId/employee number)
    if (filter?.employeeNumber) {
      params.set('filters[driverId][$containsi]', filter.employeeNumber)
    }

    // Binded filter (whether driver is assigned to a vehicle/resource)
    if (filter?.isBinded !== undefined && filter.isBinded !== 'all') {
      if (filter.isBinded === true) {
        params.set('filters[resources][$notNull]', 'true')
      } else {
        params.set('filters[resources][$null]', 'true')
      }
    }

    if (filter?.search) {
      params.set('filters[$or][0][name][$containsi]', filter.search)
      params.set('filters[$or][1][licenseNumber][$containsi]', filter.search)
      params.set('filters[$or][2][phone][$containsi]', filter.search)
      params.set('filters[$or][3][licenseType][$containsi]', filter.search)
    }

    Object.entries(DEFAULT_DRIVER_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })

    const url = `${STRAPI_DRIVER_ENDPOINTS.LIST}?${params.toString()}`
    const response = await http.get<StrapiDriverListResponse>(url)

    try {
      const validated = strapiDriverListResponseSchema.parse(response.data)
      return transformStrapiDriversToDomain(validated.data)
    } catch (error) {
      console.error('Strapi response validation error (drivers/list):', error)
      console.error('Response data:', response.data)
      if (response.data?.data && Array.isArray(response.data.data)) {
        return transformStrapiDriversToDomain(response.data.data as any)
      }
      throw error
    }
  },

  /**
   * Get drivers with pagination
   */
  async getDriversPaginated(filter?: DriversFilter): Promise<PaginatedDrivers> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    // Pagination
    const page = filter?.page ?? 1
    const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE
    params.set('pagination[page]', String(page))
    params.set('pagination[pageSize]', String(pageSize))

    // Status filter - supports single value or array
    if (filter?.status && filter.status !== 'all') {
      if (Array.isArray(filter.status)) {
        // Multiple statuses - use $in operator
        filter.status.forEach((status, index) => {
          const backendStatus = driverStatusToBackend[status]
          params.set(`filters[driverStatus][$in][${index}]`, backendStatus)
        })
      } else {
        // Single status
        const backendStatus = driverStatusToBackend[filter.status]
        params.set('filters[driverStatus][$eq]', backendStatus)
      }
    }

    // License type filter - supports single value or array
    if (filter?.licenseType && filter.licenseType !== 'all') {
      if (Array.isArray(filter.licenseType)) {
        // Multiple license types - use $in operator
        filter.licenseType.forEach((lt, index) => {
          params.set(`filters[licenseType][$in][${index}]`, lt)
        })
      } else {
        // Single license type
        params.set('filters[licenseType][$eq]', filter.licenseType)
      }
    }

    // Name filter (search by driver name)
    if (filter?.name) {
      params.set('filters[name][$containsi]', filter.name)
    }

    // Phone filter (search by phone number)
    if (filter?.phone) {
      params.set('filters[phone][$containsi]', filter.phone)
    }

    // Employee number filter (search by driverId/employee number)
    if (filter?.employeeNumber) {
      params.set('filters[driverId][$containsi]', filter.employeeNumber)
    }

    // Binded filter (whether driver is assigned to a vehicle/resource)
    if (filter?.isBinded !== undefined && filter.isBinded !== 'all') {
      if (filter.isBinded === true) {
        // Driver is binded - has a resource assignment
        params.set('filters[resources][$notNull]', 'true')
      } else {
        // Driver is not binded - has no resource assignment
        params.set('filters[resources][$null]', 'true')
      }
    }

    if (filter?.search) {
      params.set('filters[$or][0][name][$containsi]', filter.search)
      params.set('filters[$or][1][licenseNumber][$containsi]', filter.search)
      params.set('filters[$or][2][phone][$containsi]', filter.search)
      params.set('filters[$or][3][licenseType][$containsi]', filter.search)
    }

    // Sorting - map frontend field names to Strapi field names
    if (filter?.sortBy) {
      const sortFieldMap: Record<string, string> = {
        name: 'name',
        licenseExpiry: 'licenseExpiry',
        status: 'driverStatus',
        createdAt: 'createdAt',
      }
      const strapiField = sortFieldMap[filter.sortBy] || filter.sortBy
      const sortDirection = filter.sortOrder === 'desc' ? ':desc' : ':asc'
      params.set('sort[0]', `${strapiField}${sortDirection}`)
    } else {
      // Default sort by name ascending
      params.set('sort[0]', 'name:asc')
    }

    Object.entries(DEFAULT_DRIVER_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })

    const url = `${STRAPI_DRIVER_ENDPOINTS.LIST}?${params.toString()}`
    const response = await http.get<StrapiDriverListResponse>(url)

    // Extract pagination from response
    const meta = (response.data as any)?.meta?.pagination
    const pagination: PaginationMeta = {
      page: meta?.page ?? page,
      pageSize: meta?.pageSize ?? pageSize,
      pageCount: meta?.pageCount ?? 1,
      total: meta?.total ?? 0,
    }

    try {
      const validated = strapiDriverListResponseSchema.parse(response.data)
      return {
        data: transformStrapiDriversToDomain(validated.data),
        pagination,
      }
    } catch (error) {
      console.error('Strapi response validation error (drivers/list paginated):', error)
      if (response.data?.data && Array.isArray(response.data.data)) {
        return {
          data: transformStrapiDriversToDomain(response.data.data as any),
          pagination,
        }
      }
      throw error
    }
  },

  /**
   * Get a single driver by documentId
   */
  async getDriver(documentId: string): Promise<Driver | null> {
    const http = getHttpClient()
    const params = new URLSearchParams()
    Object.entries(DEFAULT_DRIVER_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })

    const url = `${STRAPI_DRIVER_ENDPOINTS.DETAIL(documentId)}?${params.toString()}`

    try {
      const response = await http.get<StrapiDriverResponse>(url)
      try {
        const validated = strapiDriverResponseSchema.parse(response.data)
        return transformStrapiDriverToDomain(validated.data)
      } catch (error) {
        console.error('Strapi response validation error (drivers/detail):', error)
        console.error('Response data:', response.data)
        if ((response.data as any)?.data) {
          return transformStrapiDriverToDomain((response.data as any).data)
        }
        return null
      }
    } catch (error) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Create a new driver
   */
  async createDriver(driver: Partial<Driver> & { tenantId: string }): Promise<Driver> {
    const http = getHttpClient()
    const requestData = transformDriverToStrapiCreate(driver)

    const response = await http.post(STRAPI_DRIVER_ENDPOINTS.CREATE, requestData)

    const respData: any = (response as any).data
    if (respData?.data) {
      try {
        const validated = strapiDriverResponseSchema.parse(respData)
        return transformStrapiDriverToDomain(validated.data)
      } catch (error) {
        console.error('Strapi response validation error (drivers/create):', error)
        console.error('Response data:', respData)
        return transformStrapiDriverToDomain(respData.data)
      }
    }

    if (respData?.id && respData?.documentId) {
      return transformStrapiDriverToDomain(respData)
    }

    throw new Error('Unexpected createDriver response shape')
  },

  /**
   * Update an existing driver
   */
  async updateDriver(documentId: string, driver: Partial<Driver>): Promise<EntityUpdateResult<Driver>> {
    const http = getHttpClient()
    const requestData = transformDriverToStrapiUpdate(driver)

    const response = await http.put(STRAPI_DRIVER_ENDPOINTS.UPDATE(documentId), requestData)

    const respData: any = (response as any).data
    const deactivation = {
      affectedOrders: (respData?.affectedOrders as string[]) ?? [],
      affectedResources: (respData?.affectedResources as string[]) ?? [],
    }

    let entity: Driver
    if (respData?.data) {
      try {
        const validated = strapiDriverResponseSchema.parse(respData)
        entity = transformStrapiDriverToDomain(validated.data)
      } catch (error) {
        console.error('Strapi response validation error (drivers/update):', error)
        console.error('Response data:', respData)
        entity = transformStrapiDriverToDomain(respData.data)
      }
    } else if (respData?.id && respData?.documentId) {
      entity = transformStrapiDriverToDomain(respData)
    } else {
      throw new Error('Unexpected updateDriver response shape')
    }

    return { entity, deactivation }
  },

  /**
   * Delete a driver
   */
  async deleteDriver(documentId: string): Promise<void> {
    const http = getHttpClient()
    await http.delete(STRAPI_DRIVER_ENDPOINTS.DELETE(documentId))
  },

  /**
   * Export all drivers matching current filters (no pagination limit)
   */
  async exportAll(filter?: DriversFilter): Promise<Driver[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    if (filter?.status && filter.status !== 'all') {
      const backendStatus = driverStatusToBackend(filter.status as DriverStatus)
      if (backendStatus) params.set('status', backendStatus)
    }
    if (filter?.search) params.set('search', filter.search)

    const response = await http.get<unknown>(`${STRAPI_DRIVER_ENDPOINTS.LIST}/export?${params}`)
    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data) ? responseData.data : []
    return transformStrapiDriversToDomain(raw as Parameters<typeof transformStrapiDriversToDomain>[0])
  },

  async bulkImport(rows: Array<Record<string, string>>): Promise<{ created: number; skipped: number; errors: string[] }> {
    const http = getHttpClient()
    const response = await http.post<unknown>(STRAPI_DRIVER_ENDPOINTS.BULK_IMPORT, { data: rows })
    return response.data as { created: number; skipped: number; errors: string[] }
  },
}

